import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Clock, Calendar, ArrowLeftRight, ClipboardList, BarChart3, TrendingUp, FolderOpen, Users, BookOpen, Compass, Sparkles, LayoutTemplate, MapPin, CheckSquare, Megaphone, CreditCard, Smartphone, ShieldCheck, DollarSign, BarChart2, Lightbulb, Play, FileText, Wallet, Mail, Download, FileDown, AlertCircle, UserPlus, Bell, Award, Ticket, Gift, Bot, Mailbox, Briefcase, Palmtree, RefreshCw, CheckCircle2 } from 'lucide-react';
import { triggerTourRestart } from '../components/ProductTour';

const ICONS = {
  'time-clock':              Clock,
  'schedule':                Calendar,
  'schedule-templates':      LayoutTemplate,
  'shift-swaps':             ArrowLeftRight,
  'timesheets':              ClipboardList,
  'reports':                 BarChart3,
  'labor-dashboard':         BarChart2,
  'executive-insights':      TrendingUp,
  'documents':               FolderOpen,
  'staff':                   Users,
  'eva-ai':                  Sparkles,
  'eva-insights':            Lightbulb,
  'multi-location':          MapPin,
  'tasks':                   CheckSquare,
  'announcements':           Megaphone,
  'billing':                 CreditCard,
  'mobile-app':              Smartphone,
  'permissions':             ShieldCheck,
  'overtime-holiday-pay':    DollarSign,
  'payroll-run':             Play,
  'payslips-explained':      FileText,
  'cash-advances':           Wallet,
  'payslip-emails':          Mail,
  'csv-export':              Download,
  'payroll-summary-pdf':     FileDown,
  'payroll-troubleshooting': AlertCircle,
  'sendpulse-smtp':          Mail,
  'resend-smtp':             Mail,
  'invite-staff':            UserPlus,
  'trial-billing':           CreditCard,
  'notifications':           Bell,
  'timesheet-export':        Download,
  'kpi-bonus':               Award,
  // ── New feature articles ─────────────────────────────────────────────────
  'lifetime-marketplace':      Ticket,
  'lifetime-activation':       Gift,
  'ai-workforce-insights':     Bot,
  'smart-digests':             Mailbox,
  'payroll-summary-estimated': FileDown,
  'labor-productivity':        BarChart2,
  'compliance-tracking':       ShieldCheck,
  'leave-management':          Palmtree,
  'shift-replacement':         RefreshCw,
  'approval-pipelines':        CheckCircle2,
};

const CATEGORIES = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    description: 'Set up your account, install the app, and launch your first location.',
    articles: ['staff', 'invite-staff', 'time-clock', 'mobile-app', 'trial-billing', 'lifetime-activation'],
  },
  {
    id: 'core-features',
    label: 'Core Features',
    description: 'The essential day-to-day tools your team will use every day.',
    articles: ['time-clock', 'schedule', 'schedule-templates', 'notifications', 'timesheet-export'],
  },
  {
    id: 'workforce',
    label: 'Workforce Management',
    description: 'Manage shift coverage, tasks, review hours, and handle payroll data.',
    articles: ['shift-swaps', 'shift-replacement', 'leave-management', 'timesheets', 'overtime-holiday-pay', 'tasks', 'staff', 'kpi-bonus'],
  },
  {
    id: 'admin',
    label: 'Administration',
    description: 'Locations, documents, announcements, billing, roles, and operational housekeeping.',
    articles: ['multi-location', 'documents', 'announcements', 'billing', 'trial-billing', 'permissions', 'approval-pipelines', 'compliance-tracking', 'resend-smtp', 'invite-staff', 'notifications', 'lifetime-marketplace'],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    description: 'Run payroll, manage payslips, export data, automate email delivery, and handle cash advances.',
    articles: ['payroll-run', 'payslips-explained', 'payroll-summary-estimated', 'kpi-bonus', 'cash-advances', 'payslip-emails', 'csv-export', 'payroll-summary-pdf', 'payroll-troubleshooting'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Understand your labor data, track performance, and identify inefficiencies over time.',
    articles: ['labor-dashboard', 'labor-productivity', 'eva-insights', 'reports', 'executive-insights'],
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    description: 'Set up Eva chat and use Eva Insights to reduce costs and detect risks automatically.',
    articles: ['eva-insights', 'eva-ai', 'ai-workforce-insights', 'smart-digests', 'ai-integrations'],
  },
];

const ALL_MODULES = [
  { slug: 'time-clock',         title: 'Time Clock',                description: 'Track employee clock-in and clock-out with PIN-based kiosk, mobile, and desktop.',                  category: 'Core Features' },
  { slug: 'schedule',           title: 'Schedule',                  description: 'Drag-and-drop weekly schedules with bulk creation, publish/unpublish, and copy last week.',        category: 'Core Features' },
  { slug: 'schedule-templates', title: 'Weekly Schedule Templates', description: 'Save and reuse weekly shift patterns — load a template into any week in one click.',               category: 'Core Features' },
  { slug: 'shift-swaps',        title: 'Shift Swaps',               description: 'Allow employees to request shift exchanges with manager approval.',                               category: 'Workforce Management' },
  { slug: 'timesheets',         title: 'Timesheets & Payroll',      description: 'Review worked hours, approve entries, and export payroll summaries by employee.',                  category: 'Workforce Management' },
  { slug: 'overtime-holiday-pay', title: 'Overtime & Holiday Pay',   description: 'Configure daily overtime thresholds, pay multipliers, and holiday dates for payroll calculations.', category: 'Workforce Management' },
  { slug: 'payroll-run',          title: 'Run Payroll',               description: 'Generate payslips from approved timesheets, review pay data, then finalize and export.',             category: 'Payroll' },
  { slug: 'payslips-explained',   title: 'Understanding Payslips',    description: 'Learn what each line on a payslip means — regular pay, overtime, deductions, and net pay.',          category: 'Payroll' },
  { slug: 'cash-advances',        title: 'Cash Advances',             description: 'Track employee advances, auto-deduct repayments each payroll cycle, and monitor balances.',           category: 'Payroll' },
  { slug: 'payslip-emails',       title: 'Email Payslips',            description: 'Payslips are emailed automatically on finalization. Resend failed deliveries in one click.',           category: 'Payroll' },
  { slug: 'csv-export',           title: 'CSV Export',                description: 'Download a payroll run as a CSV for Excel, Google Sheets, or your accounting software.',              category: 'Payroll' },
  { slug: 'payroll-summary-pdf',  title: 'Payroll Summary PDF',       description: 'Download a one-page management report with totals, hours, and per-employee breakdown.',               category: 'Payroll' },
  { slug: 'payroll-troubleshooting', title: 'Payroll — Common Issues', description: 'Diagnose missing employees, stuck emails, locked runs, and export errors in under two minutes.',    category: 'Payroll' },
  { slug: 'kpi-bonus',          title: 'Supervisor KPI & Bonus Payouts', description: 'Score every supervisor shift on service quality and automatically pay $5 per GREEN shift.',       category: 'Payroll' },
  { slug: 'tasks',              title: 'Tasks & Checklists',        description: 'Assign daily tasks to staff, track completion, and manage operational workflows.',                 category: 'Workforce Management' },
  { slug: 'reports',            title: 'Reports',                   description: 'Analyze labor hours, staffing trends, and export operational data to CSV.',                       category: 'Analytics' },
  { slug: 'labor-dashboard',   title: 'Labor Dashboard',           description: 'Visualize labor spending and cost per employee with date range filters and CSV export.',           category: 'Analytics' },
  { slug: 'eva-insights',      title: 'Eva Insights',              description: 'Deterministic recommendations ranked by estimated savings — no AI setup, no API key required.',    category: 'Analytics' },
  { slug: 'executive-insights', title: 'Executive Insights',        description: 'Monitor workforce efficiency, budget vs. actual, and staffing risk across all locations.',         category: 'Analytics' },
  { slug: 'multi-location',     title: 'Multi-Location Management', description: 'Run all branches from one account with location-based scheduling and data isolation.',            category: 'Administration' },
  { slug: 'documents',          title: 'Documents & Acknowledgements', description: 'Upload policies and training docs, share with staff, and track acknowledgements.',            category: 'Administration' },
  { slug: 'announcements',      title: 'Announcements',             description: 'Broadcast updates to all staff or specific locations with pinned announcements.',                 category: 'Administration' },
  { slug: 'staff',              title: 'Staff Management',          description: 'Add employees, manage roles, assign locations, and configure Time Clock PIN access.',              category: 'Administration' },
  { slug: 'billing',            title: 'Billing & Subscription',    description: 'Manage your plan, view invoices, and update payment details via Stripe.',                         category: 'Administration' },
  { slug: 'permissions',        title: 'Permissions & Roles',       description: 'Control access with Owner, Manager, and Staff roles across all features.',                        category: 'Administration' },
  { slug: 'resend-smtp',        title: 'Email Notifications (Resend)', description: 'Set up automatic email delivery for shift assignments, announcements, and payslips using your own Resend account.', category: 'Administration' },
  { slug: 'invite-staff',       title: 'How to Invite Staff',       description: 'Add team members so they can access schedules, clock in, and receive updates.',                         category: 'Administration' },
  { slug: 'trial-billing',      title: 'Free Trial & Billing',      description: 'Understand your trial, subscription plan, and how to upgrade at any time.',                            category: 'Administration' },
  { slug: 'notifications',      title: 'How Notifications Work',    description: 'See announcements, schedule updates, and workforce alerts in one inbox.',                               category: 'Core Features' },
  { slug: 'timesheet-export',   title: 'Export your timesheets (CSV)', description: 'Download timesheets for payroll, accounting, or QuickBooks / Xero imports.',                       category: 'Core Features' },
  { slug: 'mobile-app',         title: 'Mobile & App Experience',   description: 'Install ReStaff as a PWA on iOS, Android, or desktop — no app store needed.',                     category: 'Getting Started' },
  { slug: 'eva-ai',             title: 'Eva AI Chat — Setup Guide', description: 'Connect your AI provider (OpenAI, Gemini, Claude, etc.) to enable Eva chat for scheduling Q&A.',  category: 'AI Assistant' },
  { slug: 'mcp',                title: 'MCP — Connect ChatGPT & Claude', description: 'Securely connect ChatGPT and Claude to your ReStaff workspace via OAuth — scoped, read-only, revocable.', category: 'AI Assistant' },
  // ── New feature articles ───────────────────────────────────────────────
  { slug: 'lifetime-marketplace',      title: 'Lifetime Marketplace (Admin)',                       description: 'Generate, export, edit, disable, and bulk-manage AppSumo / DealMirror lifetime codes.',                                        category: 'Administration' },
  { slug: 'lifetime-activation',       title: 'Lifetime License Activation',                        description: 'Marketplace customers: redeem your code, verify email, and activate your lifetime license.',                                   category: 'Getting Started' },
  { slug: 'ai-workforce-insights',     title: 'AI Workforce Insights & EVA Recommendations',        description: 'Dollar-quantified recommendations ranked by impact — overtime, idle time, workload imbalance.',                                  category: 'AI Assistant' },
  { slug: 'smart-digests',             title: 'Smart Digests & Automation Recipes',                 description: 'Daily/weekly digests, shift risk detection, and automation recipes that act without you.',                                       category: 'AI Assistant' },
  { slug: 'ai-integrations',           title: 'AI Integrations (ChatGPT, Claude, Gemini)',           description: 'Connect ChatGPT / Claude / Gemini securely via the Model Context Protocol — read-only, scoped, revocable.',                            category: 'AI Assistant' },
  { slug: 'payroll-summary-estimated', title: 'Payroll Summary & Estimated Pay Preview',            description: 'Preview projected pay before period close; download the one-page Payroll Summary PDF.',                                         category: 'Payroll' },
  { slug: 'labor-productivity',        title: 'Labor vs Productivity, RPLH & Budget Target',         description: 'Revenue Per Labor Hour, Labor Budget Target, Workforce Analytics, Idle Time & Workload Balancing.',                              category: 'Analytics' },
  { slug: 'compliance-tracking',       title: 'Compliance, Break Tracking & GPS Clock Logic',       description: 'Audit-grade break compliance, missed-punch alerts, and optional geo-fenced clock-in.',                                          category: 'Administration' },
  { slug: 'leave-management',          title: 'Leave Management',                                   description: 'Vacation, sick, and unpaid leave with approval queues, calendar view, and CSV import.',                                         category: 'Workforce Management' },
  { slug: 'shift-replacement',         title: 'Shift Replacement Workflow & Coverage Health',       description: 'Employees request replacements; eligible coworkers accept; managers see coverage status live.',                                  category: 'Workforce Management' },
  { slug: 'approval-pipelines',        title: 'Approval Pipelines',                                 description: 'Multi-step approval routing for leave, replacements, timesheet edits, and payroll runs.',                                       category: 'Administration' },
];

export default function HelpCenter() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const handleStartTour = () => {
    triggerTourRestart();
    navigate('/dashboard');
  };

  const filtered = query.trim()
    ? ALL_MODULES.filter(
        (m) =>
          m.title.toLowerCase().includes(query.toLowerCase()) ||
          m.description.toLowerCase().includes(query.toLowerCase()) ||
          m.category.toLowerCase().includes(query.toLowerCase())
      )
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={20} className="text-orange-500" />
                <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Documentation</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Help Center
              </h1>
              <p className="mt-2 text-slate-500 text-base">
                Guides, walkthroughs, and troubleshooting for every ReStaff module.
              </p>
            </div>
            <button
              onClick={handleStartTour}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm flex-shrink-0"
              data-testid="start-product-tour-btn"
            >
              <Compass size={15} />
              Interactive Tour
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 shadow-sm"
              data-testid="help-search-input"
            />
          </div>
        </div>

        {/* Search results */}
        {filtered !== null && (
          <div className="mb-10">
            {filtered.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-8">No articles found for "{query}"</p>
            ) : (
              <div>
                <p className="text-xs text-slate-400 mb-3">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{query}"</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filtered.map((m) => <ModuleCard key={m.slug} {...m} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category sections (hidden during search) */}
        {filtered === null && (
          <div className="space-y-10">
            {CATEGORIES.map((cat) => {
              const articles = ALL_MODULES.filter((m) => cat.articles.includes(m.slug));
              return (
                <section key={cat.id} data-testid={`help-category-${cat.id}`}>
                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {cat.label}
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">{cat.description}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {articles.map((m) => <ModuleCard key={m.slug} {...m} />)}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-400">
            Can't find what you need?{' '}
            <Link to="/support" className="text-orange-500 hover:text-orange-600 font-medium">
              Contact Support
            </Link>
            {' '}or{' '}
            <Link to="/feature-request" className="text-orange-500 hover:text-orange-600 font-medium">
              Request a Feature
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}

function ModuleCard({ slug, title, description }) {
  const Icon = ICONS[slug] || BookOpen;
  return (
    <Link
      to={`/help/${slug}`}
      className="flex items-start gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-orange-300 hover:shadow-md transition-all group"
      data-testid={`help-module-${slug}`}
    >
      <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors mt-0.5">
        <Icon size={17} className="text-orange-500" />
      </div>
      <div>
        <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors text-sm">
          {title}
        </h3>
        <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">
          {description}
        </p>
        <span className="mt-2 inline-block text-xs text-orange-500 font-medium">
          Read guide →
        </span>
      </div>
    </Link>
  );
}
